import React, { useState, useRef, useEffect } from 'react';
import { Button } from 'antd';
import dayjs from 'dayjs';
import { TPrescriptionListItem } from 'types/textVisit';
import useTimezone from 'hooks/useTimezone';
import s from './s.module.less';

// 图标组件
const CalendarIcon: React.FC = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M6 5.25V2.25M12 5.25V2.25M5.25 8.25H12.75M3.75 15.75H14.25C15.0784 15.75 15.75 15.0784 15.75 14.25V5.25C15.75 4.42157 15.0784 3.75 14.25 3.75H3.75C2.92157 3.75 2.25 4.42157 2.25 5.25V14.25C2.25 15.0784 2.92157 15.75 3.75 15.75Z"
            stroke="#6B7280"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const ClockIcon: React.FC<{ size?: number }> = ({ size = 16 }) => {
    return (
        <svg width={size} height={size} viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M9 6V9L11.25 11.25M15.75 9C15.75 12.7279 12.7279 15.75 9 15.75C5.27208 15.75 2.25 12.7279 2.25 9C2.25 5.27208 5.27208 2.25 9 2.25C12.7279 2.25 15.75 5.27208 15.75 9Z"
                stroke="#111827"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
};

const DocumentIcon: React.FC = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M6.75 9H11.25M6.75 12H11.25M12.75 15.75H5.25C4.42157 15.75 3.75 15.0784 3.75 14.25V3.75C3.75 2.92157 4.42157 2.25 5.25 2.25H9.43934C9.63825 2.25 9.82902 2.32902 9.96967 2.46967L14.0303 6.53033C14.171 6.67098 14.25 6.86175 14.25 7.06066V14.25C14.25 15.0784 13.5784 15.75 12.75 15.75Z"
            stroke="#6B7280"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const MedicineIcon: React.FC = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M14.25 1.5V3H12.75V5.25C13.3467 5.25 13.919 5.48705 14.341 5.90901C14.7629 6.33097 15 6.90326 15 7.5V15.75C15 15.9489 14.921 16.1397 14.7803 16.2803C14.6397 16.421 14.4489 16.5 14.25 16.5H3.75C3.55109 16.5 3.36032 16.421 3.21967 16.2803C3.07902 16.1397 3 15.9489 3 15.75V7.5C3 6.90326 3.23705 6.33097 3.65901 5.90901C4.08097 5.48705 4.65326 5.25 5.25 5.25V3H3.75V1.5H14.25ZM12.75 6.75H5.25C5.05109 6.75 4.86032 6.82902 4.71967 6.96967C4.57902 7.11032 4.5 7.30109 4.5 7.5V15H13.5V7.5C13.5 7.30109 13.421 7.11032 13.2803 6.96967C13.1397 6.82902 12.9489 6.75 12.75 6.75ZM9.75 8.25V9.75H11.25V11.25H9.74925L9.75 12.75H8.25L8.24925 11.25H6.75V9.75H8.25V8.25H9.75ZM11.25 3H6.75V5.25H11.25V3Z"
            fill="#6B7280"
        />
    </svg>
);

const PaperclipIcon: React.FC = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M11.3787 5.25L6.43934 10.1893C5.85355 10.7751 5.85355 11.7249 6.43934 12.3107C7.02513 12.8964 7.97487 12.8964 8.56066 12.3107L13.3713 7.37132C14.5429 6.19975 14.5429 4.30025 13.3713 3.12868C12.1997 1.95711 10.3003 1.95711 9.12868 3.12868L4.31802 8.06802C2.56066 9.82538 2.56066 12.6746 4.31802 14.432C6.07538 16.1893 8.92462 16.1893 10.682 14.432L15.375 9.75"
            stroke="#6B7280"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

interface TreatmentPlanCardProps {
    appointmentName: string;
    startAt: string;
    endAt: string;
    duration: number;
    hasTextVisitTreatment?: boolean;
    prescriptions?: TPrescriptionListItem[];
    treatmentPlanText?: string;
    attachmentsCount?: number;
    submittedAt?: string;
    lastUpdatedAt?: string;
    status?: 'IN_PROGRESS' | 'OVERDUE' | 'SENT'; // 治疗计划状态
    isDismissed?: boolean; // 是否被驳回
    onButtonClick?: () => void; // 按钮点击事件
    onClickViewIntake?: () => void; // 查看治疗计划点击事件
    itemType?: 'TEXT_VISIT' | 'APPOINTMENT' | 'STANDALONE';
    // 是否为 medication / prescription refill 类型的 Text visit
    isRefillVisit?: boolean;
    providerName?: string;
}

/**
 * 治疗计划卡片组件
 * 用于显示 IN_PROGRESS 状态的预约信息
 */
const TreatmentPlanCard = ({
    appointmentName,
    startAt,
    endAt,
    duration,
    hasTextVisitTreatment = false,
    prescriptions = [],
    treatmentPlanText,
    attachmentsCount = 0,
    submittedAt,
    lastUpdatedAt,
    status = 'IN_PROGRESS',
    isDismissed = false,
    onButtonClick,
    onClickViewIntake,
    itemType = 'APPOINTMENT',
    isRefillVisit = false,
    providerName,
}: TreatmentPlanCardProps) => {
    const [expandedTreatment, setExpandedTreatment] = useState(false);
    const [expandedPrescription, setExpandedPrescription] = useState(false);
    const [isTreatmentTruncated, setIsTreatmentTruncated] = useState(false);
    const treatmentContentRef = useRef<HTMLSpanElement>(null);
    const [, myTimezone] = useTimezone();
    /**
     * 计算状态标签
     * 根据 Figma 设计规则：
     * - Day 0 (访问结束当天): "3 days left" - 灰色 (#F3F4F6)
     * - Day 1: "2 days left" - 黄色 (#FEF3C7)
     * - Day 2: "1 day left" - 浅红色 (#FEF2F2)
     * - Day 3: "Due today" - 红色 (#FEE2E2)
     * - >3 days: "Overdue" - 红色 (#FECACA)
     * - Dismissed: "Dismissed" - 红色 (#FEE2E2)
     */
    const getStatusBadge = (): { label: string; className: string } => {
        if (isDismissed) {
            return { label: 'Dismissed', className: s.statusBadgeRed };
        }

        if (status === 'SENT') {
            return { label: '', className: '' };
        }

        // medication / prescription refill 不显示 daysSinceEnd 倒计时标签
        if (isRefillVisit) {
            return { label: '', className: '' };
        }

        if (status === 'OVERDUE') {
            return { label: 'Overdue', className: s.statusBadgeDarkRed };
        }

        if (!endAt) {
            return { label: 'Due today', className: s.statusBadgeRed };
        }

        const endDate = dayjs(endAt);
        const today = dayjs().startOf('day');
        const endDateStart = endDate.startOf('day');
        const daysSinceEnd = today.diff(endDateStart, 'day');

        if (daysSinceEnd === 0) {
            return { label: '3 days left', className: s.statusBadgeGray };
        } else if (daysSinceEnd === 1) {
            return { label: '2 days left', className: s.statusBadgeYellow };
        } else if (daysSinceEnd === 2) {
            return { label: '1 day left', className: s.statusBadgeLightRed };
        } else if (daysSinceEnd === 3) {
            return { label: 'Due today', className: s.statusBadgeRed };
        } else if (daysSinceEnd > 3) {
            return { label: 'Overdue', className: s.statusBadgeDarkRed };
        } else {
            return { label: '3 days left', className: s.statusBadgeGray };
        }
    };

    const statusBadge = getStatusBadge();
    // 2026-09-14 设计改名（全局，含 client detail Treatment plan tab / To-do 弹窗 / claims 弹窗）
    const buttonText = hasTextVisitTreatment ? 'Resume visit notes' : 'Add visit notes';

    // 格式化日期
    const formatDate = (dateStr: string) => {
        return dayjs(dateStr)
            .tz(myTimezone?.value as string)
            .format('dddd, MMMM D, YYYY');
    };

    // 格式化时间
    const formatTime = (startStr: string, endStr: string, durationMins: number) => {
        const start = dayjs(startStr).tz(myTimezone?.value as string);
        const end = dayjs(endStr).tz(myTimezone?.value as string);
        const timezone = myTimezone?.value || 'PST';
        return `${start.format('h:mm')} - ${end.format('h:mm a')} (${timezone}) ${durationMins > 0 ? `| ${durationMins} mins` : ''}`;
    };

    const formatPrescription = (prescription: TPrescriptionListItem): string => {
        const drugName = prescription.drugName || '';
        const drugLine1 = prescription.drugLine1 || '';
        const quantity = prescription.quantity || '';
        const refill = prescription.refill || 0;
        const fillDate = prescription.fillDate ? dayjs(prescription.fillDate).format('MM/DD/YY') : '';
        const prescriptionDuration = prescription.duration || 0;

        return `${drugName} ${drugLine1}, ${quantity} with ${refill} refills,${fillDate} ${prescriptionDuration} day supply`;
    };

    // 截断文本（用于普通文本，如 prescriptions）
    const truncateText = (text: string, maxLength: number = 50): string => {
        if (text.length <= maxLength) return text;
        return `${text.substring(0, maxLength)} ...`;
    };

    // 检查实际渲染后的内容是否被截断
    useEffect(() => {
        if (treatmentContentRef.current && treatmentPlanText && !expandedTreatment) {
            const element = treatmentContentRef.current;
            // 使用多个时机来确保检测准确
            const checkTruncation = () => {
                if (!element) return;

                // 创建一个临时元素测量完整高度（不应用 line-clamp）
                const tempElement = document.createElement('div');
                const computedStyle = window.getComputedStyle(element);

                // 复制所有相关样式，确保测量准确
                tempElement.style.position = 'absolute';
                tempElement.style.visibility = 'hidden';
                tempElement.style.top = '-9999px';
                tempElement.style.left = '-9999px';
                tempElement.style.width = `${element.offsetWidth || 300}px`;
                tempElement.style.padding = computedStyle.padding;
                tempElement.style.margin = computedStyle.margin;
                tempElement.style.fontSize = computedStyle.fontSize;
                tempElement.style.lineHeight = computedStyle.lineHeight;
                tempElement.style.fontFamily = computedStyle.fontFamily;
                tempElement.style.fontWeight = computedStyle.fontWeight;
                tempElement.style.letterSpacing = computedStyle.letterSpacing;
                tempElement.style.wordSpacing = computedStyle.wordSpacing;
                tempElement.style.boxSizing = computedStyle.boxSizing;
                tempElement.style.whiteSpace = computedStyle.whiteSpace;
                tempElement.style.wordWrap = computedStyle.wordWrap;
                tempElement.className = element.className;
                tempElement.innerHTML = treatmentPlanText;
                // 不应用 line-clamp，让它自然展开
                tempElement.style.display = 'block';
                tempElement.style.webkitLineClamp = 'none';
                tempElement.style.webkitBoxOrient = 'initial';
                tempElement.style.overflow = 'visible';
                tempElement.style.textOverflow = 'clip';

                document.body.appendChild(tempElement);

                // 等待浏览器渲染
                requestAnimationFrame(() => {
                    const fullHeight = tempElement.scrollHeight;
                    const clampedHeight = element.scrollHeight;
                    const clampedClientHeight = element.clientHeight;

                    // 清理临时元素
                    document.body.removeChild(tempElement);

                    // 方法1: 比较完整高度和截断后的高度
                    // 方法2: 检查 scrollHeight 是否大于 clientHeight（当应用了 line-clamp 后，如果被截断，scrollHeight 会大于 clientHeight）
                    const isTruncatedByComparison = fullHeight > clampedHeight + 2;
                    const isTruncatedByScroll = clampedHeight > clampedClientHeight + 1;

                    // 两种方法任一为 true 就认为被截断了
                    const isTruncated = isTruncatedByComparison || isTruncatedByScroll;
                    setIsTreatmentTruncated(isTruncated);
                });
            };

            // 延迟检测，确保 DOM 已完全渲染和样式已应用
            // 使用多个时机检测，确保在不同渲染阶段都能检测到
            const timer1 = setTimeout(checkTruncation, 50);
            const timer2 = setTimeout(checkTruncation, 150);
            const timer3 = setTimeout(checkTruncation, 300);
            // 使用 requestAnimationFrame 确保在下一帧检测
            requestAnimationFrame(() => {
                setTimeout(checkTruncation, 100);
            });

            return () => {
                clearTimeout(timer1);
                clearTimeout(timer2);
                clearTimeout(timer3);
            };
        } else if (!treatmentPlanText) {
            setIsTreatmentTruncated(false);
        }
    }, [treatmentPlanText, expandedTreatment]);

    return (
        <div className={s.treatmentCard}>
            <div className={s.cardContent}>
                <div className={s.leftContent}>
                    <h3 className={s.cardTitle}>
                        {appointmentName}
                        {providerName && <span className={s.fromProvider}>from {providerName}</span>}
                    </h3>

                    <div className={s.infoRow}>
                        {(() => {
                            if (itemType === 'TEXT_VISIT') {
                                if (!(submittedAt || lastUpdatedAt)) return null;
                                return (
                                    <div className={s.infoItem}>
                                        <CalendarIcon />
                                        <span className={s.infoText}>
                                            {(() => {
                                                if (submittedAt && lastUpdatedAt) {
                                                    return (
                                                        <>
                                                            Submitted on {dayjs(submittedAt).format('MMM D')} | Last updated
                                                            on {dayjs(lastUpdatedAt).format('MMM D')}
                                                        </>
                                                    );
                                                }
                                                if (submittedAt) {
                                                    return <>Submitted on {dayjs(submittedAt).format('MMM D')}</>;
                                                }
                                                if (lastUpdatedAt) {
                                                    return <>Last updated on {dayjs(lastUpdatedAt).format('MMM D')}</>;
                                                }
                                                return null;
                                            })()}
                                        </span>
                                    </div>
                                );
                            }
                            if (itemType === 'STANDALONE') {
                                if (!submittedAt) return null;
                                return (
                                    <div className={s.infoItem}>
                                        <CalendarIcon />
                                        <span className={s.infoText}>Sent on {dayjs(submittedAt).format('MMM D, YYYY')}</span>
                                    </div>
                                );
                            }
                            return (
                                <>
                                    <div className={s.infoItem}>
                                        <CalendarIcon />
                                        <span className={s.infoText}>{formatDate(startAt)}</span>
                                    </div>
                                    <div className={s.infoItem}>
                                        <ClockIcon />
                                        <span className={s.infoText}>{formatTime(startAt, endAt, duration)}</span>
                                    </div>
                                </>
                            );
                        })()}
                    </div>

                    {treatmentPlanText && (
                        <div className={s.infoRow}>
                            <div className={s.infoItem}>
                                <DocumentIcon />
                                <span className={s.infoText}>
                                    {expandedTreatment ? (
                                        <>
                                            <span
                                                className={s.richTextContent}
                                                // eslint-disable-next-line react/no-danger
                                                dangerouslySetInnerHTML={{ __html: treatmentPlanText }}
                                            />{' '}
                                            {isTreatmentTruncated && (
                                                <span
                                                    className={s.viewAllLink}
                                                    onClick={() => setExpandedTreatment(false)}
                                                >
                                                    View less
                                                </span>
                                            )}
                                        </>
                                    ) : (
                                        <span className={s.treatmentTextContainer}>
                                            <span
                                                ref={treatmentContentRef}
                                                className={`${s.richTextContent} ${s.treatmentTextTruncated}`}
                                                // eslint-disable-next-line react/no-danger
                                                dangerouslySetInnerHTML={{ __html: treatmentPlanText }}
                                            />
                                            {isTreatmentTruncated && (
                                                <span
                                                    className={`${s.viewAllLink} ${s.viewAllLinkInline}`}
                                                    onClick={() => setExpandedTreatment(true)}
                                                >
                                                    View all
                                                </span>
                                            )}
                                        </span>
                                    )}
                                </span>
                            </div>
                        </div>
                    )}

                    {prescriptions.length > 0 && (
                        <div className={s.infoRow}>
                            <div className={s.infoItem}>
                                <MedicineIcon />
                                <span className={s.infoText}>
                                    {expandedPrescription ? (
                                        <>
                                            {prescriptions.map((p, idx) => (
                                                <span key={idx}>
                                                    {formatPrescription(p)}
                                                    {idx < prescriptions.length - 1 ? ', ' : ''}
                                                </span>
                                            ))}{' '}
                                            <span
                                                className={s.viewAllLink}
                                                onClick={() => setExpandedPrescription(false)}
                                            >
                                                View less
                                            </span>
                                        </>
                                    ) : (
                                        <>
                                            {(() => {
                                                const fullText = prescriptions
                                                    .map((p) => formatPrescription(p))
                                                    .join(', ');
                                                const truncated = truncateText(fullText);
                                                const isTruncated = fullText.length > 50;
                                                return (
                                                    <>
                                                        {truncated}{' '}
                                                        {isTruncated && (
                                                            <span
                                                                className={s.viewAllLink}
                                                                onClick={() => setExpandedPrescription(true)}
                                                            >
                                                                View all
                                                            </span>
                                                        )}
                                                    </>
                                                );
                                            })()}
                                        </>
                                    )}
                                </span>
                            </div>
                        </div>
                    )}

                    {attachmentsCount > 0 && (
                        <div className={s.infoRow}>
                            <div className={s.infoItem}>
                                <PaperclipIcon />
                                <span className={s.infoText}>{attachmentsCount} attachments</span>
                            </div>
                        </div>
                    )}
                </div>

                {/* 右侧状态和按钮 */}
                <div className={s.rightContent}>
                    {status === 'SENT' ? (
                        <div className={s.actionButtons}>
                            <Button className={s.actionButton} onClick={onButtonClick}>
                                View visit notes
                            </Button>
                            {itemType !== 'STANDALONE' && (
                                <Button className={s.actionButton} onClick={onClickViewIntake}>
                                    View intake
                                </Button>
                            )}
                        </div>
                    ) : (
                        <>
                            {statusBadge.label && (
                                <div className={`${s.statusBadge} ${statusBadge.className}`}>
                                    <ClockIcon size={16} />
                                    <span className={s.badgeText}>{statusBadge.label}</span>
                                </div>
                            )}

                            <Button className={s.actionButton} onClick={onButtonClick}>
                                {buttonText}
                            </Button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TreatmentPlanCard;
